/**
 * ADD A CUSTOMER — ONE FORM (11 Oct 2026). The owner, with CTS1's "Add New Customer":
 * "take inspiration and redo ours". CTS1 captures everything at once — identity and tax,
 * billing and shipping addresses, the account's billing type, free days, deposit, credit
 * limit and payment terms, the tiered holding rates with a charge preview, and the gas
 * rates — and saves it in one go. CTS2 keeps its seven commands behind this one form: they
 * run in order on Save, and if one refuses, the customer already made is opened with the
 * guided onboarding offered to finish the rest. Nothing typed is lost.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, type Views } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Check, Select, Text, TextArea } from '../ui.js';
import { Page } from '../shell.js';
import { money, rupeesToPaise, today } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('customers')!.messages;

interface Slab { from: string; to: string; rupees: string }
interface GasRate { gas: string; rupees: string }
interface Extra { label: string; address: string; stateCode: string; pincode: string }

const GST_TYPES = [
  { value: 'regular', label: 'Regular (B2B)' }, { value: 'composition', label: 'Composition' },
  { value: 'unregistered', label: 'Unregistered / B2C' }, { value: 'sez', label: 'SEZ' }, { value: 'other', label: 'Other' },
];
const DEFAULT_SLABS: Slab[] = [{ from: '1', to: '10', rupees: '0' }, { from: '11', to: '30', rupees: '3' }, { from: '31', to: '60', rupees: '4' }, { from: '61', to: '90', rupees: '5' }, { from: '91', to: '365', rupees: '10' }];

/** CTS1's charge preview: Σ over tiers of the days of [1..D] that fall in the tier × its rate per cylinder per day. */
export function chargeFor(days: number, slabs: Slab[]): number {
  let rupees = 0;
  for (const s of slabs) {
    const from = Number(s.from), to = s.to.trim() ? Number(s.to) : Infinity, rate = Number(s.rupees || 0);
    if (!(from >= 1) || !(rate >= 0)) continue;
    const overlap = Math.max(0, Math.min(days, to) - from + 1);
    rupees += overlap * rate;
  }
  return rupees;
}

export function NewCustomer() {
  const states = useAsk(() => api.view('states.list', {}), [], MESSAGES);
  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 100 }), [], MESSAGES);
  const runner = useRun(MESSAGES);
  // identity & tax
  const [name, setName] = useState(''); const [contact, setContact] = useState(''); const [phone, setPhone] = useState(''); const [email, setEmail] = useState('');
  const [gstType, setGstType] = useState('regular'); const [gstin, setGstin] = useState(''); const [stateCode, setStateCode] = useState(''); const [pincode, setPincode] = useState('');
  const [billing, setBilling] = useState('');
  // shipping
  const [shipping, setShipping] = useState(''); const [shipState, setShipState] = useState(''); const [shipPin, setShipPin] = useState('');
  const [extras, setExtras] = useState<Extra[]>([]);
  // account & billing
  const [billingType, setBillingType] = useState<'tiered' | 'fixed'>('tiered'); const [dailyRate, setDailyRate] = useState('');
  const [freeDays, setFreeDays] = useState('10'); const [deposit, setDeposit] = useState(''); const [creditLimit, setCreditLimit] = useState('');
  const [creditDays, setCreditDays] = useState('15'); const [graceDays, setGraceDays] = useState('0'); const [interest, setInterest] = useState('0');
  const [freight, setFreight] = useState('0'); const [loading, setLoading] = useState('0'); const [unloading, setUnloading] = useState('0');
  const [slabs, setSlabs] = useState<Slab[]>(DEFAULT_SLABS);
  const [rates, setRates] = useState<GasRate[]>([{ gas: 'oxygen', rupees: '' }, { gas: 'acetylene', rupees: '' }]);
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState<string | null>(null);

  const stateOptions = (states.data?.rows ?? []).map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));
  const stateName = (code: string) => states.data?.rows.find((s) => s.code === code)?.name ?? '';
  const gasSuggestions = Array.from(new Set((skus.data?.rows ?? []).map((s) => s.gasName.toLowerCase().replace(/^dissolved /, '').replace(/[^a-z0-9]+/g, '_'))));

  // GSTIN tells the state: the first two digits are the GST state code
  function onGstin(v: string) {
    const up = v.toUpperCase().trim(); setGstin(up);
    if (/^[0-9]{2}/.test(up) && !stateCode) setStateCode(up.slice(0, 2));
  }
  const gstinOk = gstType === 'unregistered' || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin);
  const gstinStateMismatch = gstin.length === 15 && stateCode && gstin.slice(0, 2) !== stateCode;
  const canSave = name.trim() !== '' && stateCode !== '' && billing.trim() !== '' && gstinOk && !runner.busy
    && (billingType === 'tiered' ? slabs.some((s) => s.from.trim() && s.rupees.trim()) : Number(dailyRate) > 0);

  async function save() {
    const paise = (s: string) => rupeesToPaise(s) ?? 0;
    // 1. the customer and its first site
    setProgress('Creating the customer…');
    let created: { customerId: number; siteId: number } | null = null;
    const madeIt = await runner.run(async () => {
      const r = await api.command('customer.create', {
        name: name.trim(), ...(gstin && gstType !== 'unregistered' ? { gstin } : {}), state: stateName(stateCode), stateCode,
        gstRegistration: gstType as 'regular', paymentTerms: { creditDays: Number(creditDays) || 0, graceDays: Number(graceDays) || 0, interestMonthlyPct: Number(interest) || 0 },
        ...(creditLimit.trim() ? { creditLimitPaise: paise(creditLimit) } : {}), ...(deposit.trim() ? { depositPerCylinderPaise: paise(deposit) } : {}),
        primarySite: { name: shipping.trim() ? 'Delivery address' : 'Billing address', address: (shipping.trim() || billing).trim() },
      });
      created = r.value as { customerId: number; siteId: number };
    });
    const made = created as { customerId: number; siteId: number } | null;
    if (!madeIt || !made) { setProgress(null); return; }
    const customerId = made.customerId; const siteId = made.siteId;
    const stepsLeft: (() => Promise<unknown>)[] = [];
    stepsLeft.push(() => api.command('customer.setAddress', { customerId, kind: 'billing', line1: billing.trim(), stateCode, ...(pincode.trim() ? { pincode: pincode.trim() } : {}) }));
    stepsLeft.push(() => api.command('customer.setAddress', { customerId, siteId, kind: 'delivery', line1: (shipping.trim() || billing).trim(), stateCode: shipState || stateCode, ...((shipPin || pincode).trim() ? { pincode: (shipPin || pincode).trim() } : {}) }));
    if (contact.trim() || phone.trim() || email.trim()) {
      stepsLeft.push(() => api.command('customer.addContact', { customerId, name: contact.trim() || name.trim(), isPrimary: true, ...(phone.trim() ? { phone: phone.trim() } : {}), ...(email.trim() ? { email: email.trim() } : {}) }));
    }
    if (gstin && gstType !== 'unregistered') stepsLeft.push(() => api.command('customer.recordRegistration', { customerId, gstin, legalName: name.trim(), effectiveFrom: today() }));
    for (const e of extras.filter((x) => x.address.trim())) {
      stepsLeft.push(async () => {
        const s = (await api.command('site.create', { customerId, name: e.label.trim() || 'Delivery address', address: e.address.trim() })).value as { siteId: number };
        return api.command('customer.setAddress', { customerId, siteId: s.siteId, kind: 'delivery', line1: e.address.trim(), stateCode: e.stateCode || stateCode, ...(e.pincode.trim() ? { pincode: e.pincode.trim() } : {}) });
      });
    }
    const gasRates: Record<string, number> = {};
    for (const r of rates) { const g = r.gas.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_'); const p = rupeesToPaise(r.rupees); if (g && p !== null && p > 0) gasRates[g] = p; }
    // CTS1 writes the free period as a zero-rate first tier from day 1; CTS2's holding card
    // starts its slabs the day AFTER the free days and wants them contiguous. Normalise: clip
    // every tier to start after the free days, drop what falls wholly inside them, and make
    // each tier begin where the previous one ended.
    const free = Number(freeDays) || 0;
    const raw = billingType === 'tiered'
      ? slabs.filter((s) => s.from.trim()).map((s) => ({ fromDay: Number(s.from), toDay: s.to.trim() ? Number(s.to) : 100000, ratePaise: paise(s.rupees) })).sort((a, b) => a.fromDay - b.fromDay)
      : [{ fromDay: 1, toDay: 100000, ratePaise: paise(dailyRate) }];
    const slabsOut: { fromDay: number; toDay: number; ratePaise: number }[] = [];
    let next = free + 1;
    for (const t of raw) {
      const from = Math.max(t.fromDay, next); const to = Math.max(t.toDay, from);
      if (t.toDay < next) continue;
      slabsOut.push({ fromDay: from, toDay: to, ratePaise: t.ratePaise }); next = to + 1;
    }
    if (Object.keys(gasRates).length > 0) {
      stepsLeft.push(() => api.command('customer.setTerms', { customerId, gasRates, jobFillRates: {},
        holdingCard: { freeDays: Number(freeDays) || 0, slabs: slabsOut, ...(billingType === 'fixed' ? { flatRatePaise: paise(dailyRate) } : {}) },
        freightPaise: paise(freight), loadingPaisePerCyl: paise(loading), unloadingPaisePerCyl: paise(unloading),
        effectiveFrom: today(), reason: note.trim() || 'agreed when the customer was added' }));
    }
    let n = 0;
    for (const step of stepsLeft) {
      n += 1; setProgress(`Saving ${n} of ${stepsLeft.length}…`);
      const ok = await runner.run(() => step());
      if (!ok) { setProgress(`The customer was created, but step ${n} was refused — finish it on the customer's page.`); go(`#/customers/${customerId}?tab=record`); return; }
    }
    go(`#/customers/${customerId}`);
  }

  const preview = [30, 60, 90, 180, 365, 500];
  const previewSlabs = billingType === 'tiered' ? slabs : [{ from: '1', to: '', rupees: dailyRate }];

  return (
    <Page title="Add new customer" sub="Everything on one form, as before — identity and tax, where to bill and deliver, the account's terms, the holding tiers and the gas rates."
          actions={<><Button onClick={() => go('#/customers')}>Cancel</Button><Button kind="primary" disabled={!canSave} onClick={() => { void save(); }}>{runner.busy ? 'Saving…' : 'Save customer'}</Button></>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {progress ? <Banner kind="info" title={progress} /> : null}

      <Card title="Identity & tax details">
        <div className="grid two">
          <Text name="nc-name" label="Party name" value={name} onChange={setName} required autoFocus />
          <Text name="nc-contact" label="Contact person" value={contact} onChange={setContact} placeholder="Who to ask for" />
          <Text name="nc-phone" label="Phone" value={phone} onChange={setPhone} />
          <Text name="nc-email" label="Email" value={email} onChange={setEmail} />
          <Select name="nc-gsttype" label="GST registration" value={gstType} onChange={setGstType} options={GST_TYPES} />
          <Text name="nc-gstin" label="GSTIN" value={gstin} onChange={onGstin} placeholder="18ABCDE1234F1Z5"
                {...(gstType === 'unregistered' ? { help: 'Not required for unregistered / B2C' } : !gstinOk && gstin ? { error: 'A GSTIN is 15 characters: 2 digits, 5 letters, 4 digits, a letter, a digit or letter, Z, and one more' } : gstinStateMismatch ? { error: 'The GSTIN\'s first two digits do not match the state chosen' } : {})} />
          <Select name="nc-state" label="State (place of supply)" value={stateCode} onChange={setStateCode} placeholder="— select state —" options={stateOptions} />
          <Text name="nc-pin" label="Pincode" value={pincode} onChange={setPincode} />
        </div>
        <TextArea name="nc-billing" label="Billing address" value={billing} onChange={setBilling} />
      </Card>

      <Card title="Shipping / consignee" sub="Leave blank to use the billing address. Used for delivery and the challan's 'Ship to'; GST follows the billing state.">
        <TextArea name="nc-shipping" label="Shipping address" value={shipping} onChange={setShipping} placeholder="Where goods are delivered — leave blank if same as billing" />
        <div className="grid two">
          <Select name="nc-shipstate" label="Shipping / delivery state" value={shipState} onChange={setShipState} placeholder="— same as billing —" options={stateOptions} />
          <Text name="nc-shippin" label="Shipping pincode" value={shipPin} onChange={setShipPin} />
        </div>
        {extras.map((e, i) => (
          <div key={i} className="grid three" style={{ alignItems: 'end', borderTop: '1px solid var(--line, #e3e6ea)', paddingTop: 8 }}>
            <Text name={`nc-x-label-${i}`} label="Label" value={e.label} onChange={(v) => setExtras((o) => o.map((x, k) => k === i ? { ...x, label: v } : x))} placeholder="Site, plant, depot…" />
            <Text name={`nc-x-addr-${i}`} label="Address" value={e.address} onChange={(v) => setExtras((o) => o.map((x, k) => k === i ? { ...x, address: v } : x))} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
              <Select name={`nc-x-state-${i}`} label="State" value={e.stateCode} onChange={(v) => setExtras((o) => o.map((x, k) => k === i ? { ...x, stateCode: v } : x))} placeholder="— as billing —" options={stateOptions} />
              <Text name={`nc-x-pin-${i}`} label="PIN" value={e.pincode} onChange={(v) => setExtras((o) => o.map((x, k) => k === i ? { ...x, pincode: v } : x))} />
              <Button small kind="danger" onClick={() => setExtras((o) => o.filter((_, k) => k !== i))}>×</Button>
            </div>
          </div>
        ))}
        <Button small onClick={() => setExtras((o) => [...o, { label: '', address: '', stateCode: '', pincode: '' }])}>+ Add shipping address</Button>
      </Card>

      <Card title="Account & billing">
        <div className="grid three">
          <Select name="nc-billtype" label="Billing type" value={billingType} onChange={(v) => setBillingType(v as 'tiered' | 'fixed')} options={[{ value: 'tiered', label: 'Tiered (by days held)' }, { value: 'fixed', label: 'Fixed daily rate' }]} />
          {billingType === 'fixed' ? <Text name="nc-daily" label="Daily rate (₹/cyl/day)" type="number" value={dailyRate} onChange={setDailyRate} /> : <Text name="nc-free" label="Free days" type="number" value={freeDays} onChange={setFreeDays} />}
          <Text name="nc-deposit" label="Security deposit per cylinder (₹)" type="number" value={deposit} onChange={setDeposit} />
          <Text name="nc-credit" label="Credit limit (₹, blank = none)" type="number" value={creditLimit} onChange={setCreditLimit} />
          <Text name="nc-creditdays" label="Payment terms: credit days" type="number" value={creditDays} onChange={setCreditDays} />
          <Text name="nc-grace" label="Grace days" type="number" value={graceDays} onChange={setGraceDays} />
          <Text name="nc-interest" label="Interest % per month" type="number" value={interest} onChange={setInterest} />
          <Text name="nc-freight" label="Freight per trip (₹)" type="number" value={freight} onChange={setFreight} />
          <Text name="nc-loading" label="Loading / unloading per cylinder (₹)" type="number" value={loading} onChange={(v) => { setLoading(v); setUnloading(v); }} />
        </div>
      </Card>

      {billingType === 'tiered' ? (
        <Card title="Tiered holding rates" sub="Example: 10 days free → ₹1/cyl/day for the next 20 → ₹5/day to day 60 → ₹10/day after that. Day ranges run from 1; leave 'to' blank for 'and after'.">
          {slabs.map((s, i) => (
            <div key={i} className="grid three" style={{ alignItems: 'end' }}>
              <Text name={`nc-slab-from-${i}`} label="From day" type="number" value={s.from} onChange={(v) => setSlabs((o) => o.map((x, k) => k === i ? { ...x, from: v } : x))} />
              <Text name={`nc-slab-to-${i}`} label="To day (blank = ∞)" type="number" value={s.to} onChange={(v) => setSlabs((o) => o.map((x, k) => k === i ? { ...x, to: v } : x))} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
                <Text name={`nc-slab-rate-${i}`} label="Rate (₹/cyl/day)" type="number" value={s.rupees} onChange={(v) => setSlabs((o) => o.map((x, k) => k === i ? { ...x, rupees: v } : x))} />
                <Button small kind="danger" onClick={() => setSlabs((o) => o.filter((_, k) => k !== i))}>×</Button>
              </div>
            </div>
          ))}
          <Button small onClick={() => setSlabs((o) => { const last = o[o.length - 1]; const from = last?.to.trim() ? String(Number(last.to) + 1) : ''; return [...o, { from, to: '', rupees: '' }]; })}>+ Add tier</Button>
        </Card>
      ) : null}
      <Card title="Charge preview (per cylinder)" sub="what one cylinder costs the customer if held this long">
        <div className="kpis">
          {preview.map((d) => <div key={d} className="kpi"><div className="kpi-label">{d}d</div><div className="kpi-value">{money(Math.round(chargeFor(d, previewSlabs) * 100))}</div></div>)}
        </div>
      </Card>

      <Card title="Gas rates (₹ per cylinder)" sub="This customer's agreed rates per cylinder, by the gas word the yard uses (oxygen, acetylene, nitrogen…). They price every dispatch. Leave a gas blank if this customer does not buy it.">
        <datalist id="nc-gases">{gasSuggestions.map((g) => <option key={g} value={g} />)}</datalist>
        {rates.map((r, i) => (
          <div key={i} className="grid two" style={{ alignItems: 'end' }}>
            <div className="field"><label htmlFor={`nc-gas-${i}`}>Gas</label><input id={`nc-gas-${i}`} type="text" list="nc-gases" value={r.gas} style={{ width: '100%' }} onChange={(e: { target: { value: string } }) => setRates((o) => o.map((x, k) => k === i ? { ...x, gas: e.target.value } : x))} /></div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
              <Text name={`nc-rate-${i}`} label="Rate (₹ per cylinder)" type="number" value={r.rupees} onChange={(v) => setRates((o) => o.map((x, k) => k === i ? { ...x, rupees: v } : x))} />
              <Button small kind="danger" onClick={() => setRates((o) => o.filter((_, k) => k !== i))}>×</Button>
            </div>
          </div>
        ))}
        <Button small onClick={() => setRates((o) => [...o, { gas: '', rupees: '' }])}>+ Add gas rate</Button>
        <TextArea name="nc-note" label="Note on the rates (optional)" value={note} onChange={setNote} placeholder="quotation reference, who agreed it" />
      </Card>

      <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
        <Button onClick={() => go('#/customers')}>Cancel</Button>
        <Button kind="primary" disabled={!canSave} onClick={() => { void save(); }}>{runner.busy ? 'Saving…' : 'Save customer'}</Button>
      </div>
      <p className="muted">Prefer to be walked through it step by step? <a href="#/customers/new?guided=1" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/customers/new?guided=1'); }}>Use the guided onboarding.</a></p>
    </Page>
  );
}
